import { createSelector } from '@reduxjs/toolkit';
import { userRoles } from '../../utils/constants';

// Base selectors
const selectVg = state => state.vg || {};
const selectManager = state => state.manager || {};
const selectMembers = createSelector([selectVg], vg => vg.members || []);
const selectInvitedMembersRaw = createSelector([selectVg], vg => vg.invitedMembers || []);
const selectAssociateMembers = createSelector(
    [selectManager],
    manager => manager.associateMembers || []
);
const selectPropertyId = (_, propertyId) => propertyId;

// Combined list
const selectTotalMembers = createSelector(
    [selectMembers, selectInvitedMembersRaw],
    (members, invited) => [...members, ...invited]
);

const memberStatus = (m, propertyId) =>
    m?.super_suspend
        ? 'Admin Suspended'
        : m?.properties?.[propertyId]?.suspended
        ? 'Suspended'
        : 'Active';

// ===== Factory selectors per propertyId =====
// The nested `properties[propertyId].role` filter implicitly excludes
// unified invite rows (which carry `role` at the top level, not nested).
export const makeGetOccupantMembers = () =>
    createSelector([selectTotalMembers, selectPropertyId], (total, propertyId) =>
        total
            .filter(m => m?.properties?.[propertyId]?.role === userRoles.RESIDENT)
            .map(m => ({ ...m, status: memberStatus(m, propertyId) }))
    );

export const makeGetTenantMembers = () =>
    createSelector([selectTotalMembers, selectPropertyId], (total, propertyId) =>
        total
            .filter(t => t?.properties?.[propertyId]?.role === userRoles.TENANT)
            .map(t => ({ ...t, status: memberStatus(t, propertyId) }))
    );

export const makeGetPropertyGuests = () =>
    createSelector([selectTotalMembers, selectPropertyId], (total, propertyId) =>
        total
            .filter(g => g?.properties?.[propertyId]?.role === userRoles.GUEST)
            .map(g => ({
                ...g,
                status: g?.super_suspend
                    ? 'Administrative Suspension'
                    : g?.properties?.[propertyId]?.suspended
                    ? 'Suspended'
                    : 'Active'
            }))
    );

export const makeGetPropertyAssociates = () =>
    createSelector([selectTotalMembers, selectPropertyId], (total, propertyId) =>
        total
            .filter(a => a?.properties?.[propertyId]?.role === userRoles.ASSOCIATE)
            .map(a => ({
                ...a,
                status: a?.super_suspend
                    ? 'Administrative Suspension'
                    : a?.properties?.[propertyId]?.suspended
                    ? 'Suspended'
                    : 'Active'
            }))
    );

export const makeGetNonMemberOccupants = () =>
    createSelector(
        [selectAssociateMembers, selectPropertyId],
        (associateMembers, propertyId) =>
            associateMembers
                .filter(m => m.property_id === propertyId)
                .map(m => ({
                    ...m,
                    uid: m.id,
                    status: memberStatus(m, propertyId)
                }))
    );

export const makeGetOrgMembers = () =>
    createSelector([selectMembers, selectPropertyId], (members, propertyId) =>
        (members || []).filter(
            m => m?.properties?.[propertyId]?.role === 'operator'
        )
    );

const ACTIVE_INVITE_STATUSES = ['pending_approval', 'invited'];

// This one doesn't need propertyId, so it can be a shared instance
export const getPropertyInvitedMembers = createSelector([selectTotalMembers], total =>
    total.filter(m => ACTIVE_INVITE_STATUSES.includes(m?.status))
);
